import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  TooltipProvider,
} from "@/components/ui/tooltip";
import { Trophy, Flame, Star, Target, Zap, Crown, Phone, Users as UsersIcon, Calendar as CalendarIcon, ArrowUp, ArrowDown, Minus } from "lucide-react";
import {
  ShowPercentCell, CashCell, SetsCell, ClosePercentCell,
} from "./MetricBreakdownTooltips";

const ICON_MAP = { trophy: Trophy, target: Target, zap: Zap, crown: Crown, phone: Phone, users: UsersIcon, calendar: CalendarIcon, flame: Flame, star: Star };

function RankTrendCell({ rep }) {
  const change = rep.rank_change;
  const prevRank = rep.prev_rank;
  const cashPct = rep.cash_change_pct;

  if (prevRank === null || prevRank === undefined) {
    return (
      <div className="flex flex-col items-end">
        <span className="text-xs text-gray-500 italic">New</span>
        {cashPct !== undefined && cashPct !== 0 && (
          <span className="text-[10px] text-emerald-400">+{cashPct}% cash</span>
        )}
      </div>
    );
  }

  let icon, colorCls, label;
  if (change > 0) {
    icon = <ArrowUp className="w-3 h-3" />;
    colorCls = "text-emerald-400";
    label = `↑${change}`;
  } else if (change < 0) {
    icon = <ArrowDown className="w-3 h-3" />;
    colorCls = "text-red-400";
    label = `↓${Math.abs(change)}`;
  } else {
    icon = <Minus className="w-3 h-3" />;
    colorCls = "text-gray-500";
    label = "—";
  }

  const cashColor = cashPct > 0 ? "text-emerald-400" : cashPct < 0 ? "text-red-400" : "text-gray-500";
  const cashSign = cashPct > 0 ? "+" : "";

  return (
    <div className="flex flex-col items-end gap-0.5">
      <div className={`flex items-center gap-1 ${colorCls} text-xs font-medium`}>
        {icon}
        <span>#{prevRank} → #{rep.current_rank} ({label})</span>
      </div>
      {cashPct !== 0 && (
        <span className={`text-[10px] ${cashColor}`}>
          {cashSign}{cashPct}% cash
        </span>
      )}
    </div>
  );
}


export function LeaderboardTable({ leaderboard, loading, onSelectRep, getRoleBadgeClass, getRoleBadgeLetter, compareOn = false, risingUsers = {}, customColumns = [], hiddenColumns = [] }) {
  const isHidden = (key) => hiddenColumns.includes(key);
  const visibleCustomColumns = customColumns.filter((c) => !isHidden(c.key));
  const getRankIcon = (index) => {
    if (index < 3) return <Trophy className="w-5 h-5 text-yellow-400" />;
    return <span className="text-gray-500 w-5 text-center">{index + 1}</span>;
  };

  // 11 standard columns (DIALS..DQ'S) plus REP + # + optional TREND + custom
  const STANDARD_COLUMN_KEYS = ["dials", "connects", "dms", "sets", "shows", "deals", "cash", "show_percent", "close_percent", "inbound_ctas", "dqs", "financial_dqs_taken"];
  const visibleStandardCount = STANDARD_COLUMN_KEYS.filter((k) => !isHidden(k)).length;
  const colCount = 2 + visibleStandardCount + visibleCustomColumns.length + (compareOn ? 1 : 0);

  const formatCustomValue = (val, fmt) => {
    if (val === null || val === undefined || val === 0) return val === 0 ? "0" : "—";
    if (fmt === "currency") return `$${Number(val).toLocaleString()}`;
    if (fmt === "percent") return `${Number(val)}%`;
    return Number(val).toLocaleString();
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="bg-[#1A1A1A] border border-white/5 rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/5 hover:bg-transparent">
            <TableHead className="text-gray-400 font-medium w-12">#</TableHead>
            <TableHead className="text-gray-400 font-medium">REP</TableHead>
            {!isHidden("dials") && <TableHead className="text-gray-400 font-medium text-right">DIALS</TableHead>}
            {!isHidden("connects") && <TableHead className="text-gray-400 font-medium text-right">CONNECTS</TableHead>}
            {!isHidden("dms") && <TableHead className="text-gray-400 font-medium text-right">DMS</TableHead>}
            {!isHidden("sets") && <TableHead className="text-gray-400 font-medium text-right">SETS</TableHead>}
            {!isHidden("shows") && <TableHead className="text-gray-400 font-medium text-right">SHOWS</TableHead>}
            {!isHidden("deals") && <TableHead className="text-gray-400 font-medium text-right">DEALS</TableHead>}
            {!isHidden("cash") && <TableHead className="text-gray-400 font-medium text-right">CASH</TableHead>}
            {!isHidden("show_percent") && <TableHead className="text-gray-400 font-medium text-right">SHOW %</TableHead>}
            {!isHidden("close_percent") && <TableHead className="text-gray-400 font-medium text-right">CLOSE %</TableHead>}
            {!isHidden("inbound_ctas") && <TableHead className="text-gray-400 font-medium text-right">INBOUND CTA'S</TableHead>}
            {!isHidden("dqs") && <TableHead className="text-gray-400 font-medium text-right">DQ'S</TableHead>}
            {!isHidden("financial_dqs_taken") && <TableHead className="text-gray-400 font-medium text-right" title="Financial DQs on taken calls — reduces close-rate denominator">FIN DQ'S</TableHead>}
            {visibleCustomColumns.map((col) => (
              <TableHead
                key={`hdr-${col.key}`}
                className="text-emerald-300 font-medium text-right uppercase tracking-wider"
                title={`Custom column from EOD template — key: ${col.key}`}
                data-testid={`leaderboard-custom-header-${col.key}`}
              >
                {col.label}
              </TableHead>
            ))}
            {compareOn && <TableHead className="text-gray-400 font-medium text-right">TREND</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            [...Array(5)].map((_, i) => (
              <TableRow key={`skeleton-${i}`} className="border-white/5">
                <TableCell colSpan={colCount}><div className="h-12 bg-white/5 rounded animate-pulse"></div></TableCell>
              </TableRow>
            ))
          ) : leaderboard.length === 0 ? (
            <TableRow className="border-white/5">
              <TableCell colSpan={colCount} className="text-center py-12 text-gray-400">No data available for the selected period</TableCell>
            </TableRow>
          ) : (
            leaderboard.map((rep, index) => (
              <TableRow key={rep.user_id} data-testid={`leaderboard-row-${rep.user_id}`}
                data-fanfare={risingUsers[rep.user_id] || undefined}
                className={`border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
                  risingUsers[rep.user_id] === "crown"
                    ? "rank-fanfare-crown"
                    : risingUsers[rep.user_id] === "fanfare"
                      ? "rank-fanfare"
                      : ""
                }`} onClick={() => onSelectRep(rep)}>
                <TableCell className="font-medium">{getRankIcon(index)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={rep.picture} />
                      <AvatarFallback className="bg-emerald-700 text-white text-xs">{rep.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{rep.name}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${getRoleBadgeClass(rep.role)}`}>{getRoleBadgeLetter(rep.role)}</span>
                        {rep.has_eod_activity === false && (
                          <span
                            title="No EOD activity submitted in this period — show/close % unavailable"
                            className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30"
                            data-testid={`no-eod-${rep.user_id}`}
                          >
                            no EOD
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{rep.team_name}</div>
                    </div>
                  </div>
                </TableCell>
                {!isHidden("dials") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : rep.dials}</TableCell>}
                {!isHidden("connects") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : rep.connects}</TableCell>}
                {!isHidden("dms") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : rep.dms}</TableCell>}
                {!isHidden("sets") && (
                  <TableCell className="text-right">
                    <SetsCell rep={rep} />
                  </TableCell>
                )}
                {!isHidden("shows") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : rep.shows}</TableCell>}
                {!isHidden("deals") && <TableCell className="text-right text-orange-400 font-medium">{rep.deals}</TableCell>}
                {!isHidden("cash") && (
                  <TableCell className="text-right">
                    <CashCell rep={rep} />
                  </TableCell>
                )}
                {!isHidden("show_percent") && (
                  <TableCell className="text-right">
                    <ShowPercentCell rep={rep} />
                  </TableCell>
                )}
                {!isHidden("close_percent") && (
                  <TableCell className="text-right">
                    <ClosePercentCell rep={rep} />
                  </TableCell>
                )}
                {!isHidden("inbound_ctas") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : rep.inbound_ctas}</TableCell>}
                {!isHidden("dqs") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : rep.dqs}</TableCell>}
                {!isHidden("financial_dqs_taken") && <TableCell className="text-right text-white">{rep.has_eod_activity === false ? <span className="text-gray-600">—</span> : (rep.financial_dqs_taken || 0)}</TableCell>}
                {visibleCustomColumns.map((col) => {
                  const v = rep.custom_values?.[col.key];
                  return (
                    <TableCell
                      key={`cell-${rep.user_id}-${col.key}`}
                      className="text-right text-emerald-200 font-medium"
                      data-testid={`leaderboard-custom-${col.key}-${rep.user_id}`}
                    >
                      {rep.has_eod_activity === false && !v ? <span className="text-gray-600">—</span> : formatCustomValue(v, col.format)}
                    </TableCell>
                  );
                })}
                {compareOn && (
                  <TableCell className="text-right" data-testid={`leaderboard-trend-${rep.user_id}`}>
                    <RankTrendCell rep={rep} />
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
    </TooltipProvider>
  );
}

const RANK_CLASSES = [
  "bg-yellow-400/20 text-yellow-400 ring-2 ring-yellow-400/30",
  "bg-gray-300/20 text-gray-300",
  "bg-orange-400/20 text-orange-400",
];

function getRankClass(idx) { return RANK_CLASSES[idx] || "bg-white/10 text-gray-400"; }

function getTalkRatioColor(ratio) {
  if (ratio >= 40 && ratio <= 60) return "text-green-400";
  if (ratio < 30 || ratio > 70) return "text-red-400";
  return "text-yellow-400";
}

function getCoachingColor(score) {
  if (score >= 80) return "text-green-400";
  if (score >= 60) return "text-yellow-400";
  if (score > 0) return "text-orange-400";
  return "text-gray-500";
}

export function XPBoard({ xpBoard }) {
  return (
    <div className="space-y-3">
      {xpBoard.map((user, idx) => (
        <div
          key={user.user_id}
          data-testid={`xp-row-${user.user_id}`}
          className="bg-[#1A1A1A] border border-white/5 rounded-xl p-4 hover:bg-white/5 transition-colors"
        >
          {/* Header row — always horizontal: rank, avatar, name, cash */}
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${getRankClass(idx)}`}>
              {idx + 1}
            </div>
            <Avatar className="w-9 h-9 sm:w-10 sm:h-10 shrink-0">
              <AvatarFallback className="bg-emerald-700 text-white text-sm">{user.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-white font-medium truncate text-sm sm:text-base">{user.name}</div>
              <div className="text-xs text-gray-500 capitalize">{user.role}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm sm:text-base text-green-400 font-medium">${user.cash?.toLocaleString()}</div>
              <div className="text-[10px] sm:text-xs text-gray-500">{user.deals} deals</div>
            </div>
          </div>

          {/* Stats grid — wraps responsively on mobile, inline on desktop */}
          <div className="mt-3 grid grid-cols-4 sm:grid-cols-7 gap-x-2 gap-y-2 sm:gap-x-3">
            <Stat label="Level" value={user.level} valueClass="text-emerald-400 text-lg sm:text-xl font-bold" />
            <Stat label="XP" value={user.xp?.toLocaleString()} valueClass="text-white text-sm sm:text-base font-medium" />
            <Stat
              label="Streak"
              value={
                <span className="inline-flex items-center gap-1 text-orange-400">
                  <Flame className="w-3.5 h-3.5" />
                  {user.streak}d
                </span>
              }
              valueClass="text-sm sm:text-base font-medium"
            />
            <Stat
              label="Talk %"
              value={`${user.talk_ratio || 50}%`}
              valueClass={`text-sm sm:text-base font-medium ${getTalkRatioColor(user.talk_ratio || 50)}`}
            />
            <Stat label="Analyzed" value={user.calls_analyzed || 0} valueClass="text-sm sm:text-base font-medium text-blue-400" />
            <Stat
              label="Coach"
              value={user.coaching_score || "-"}
              valueClass={`text-sm sm:text-base font-medium ${getCoachingColor(user.coaching_score || 0)}`}
            />
            <div className="col-span-4 sm:col-span-1 flex items-center gap-1 sm:justify-end">
              {user.badges?.slice(0, 3).map((b) => {
                const Icon = ICON_MAP[b.icon] || Star;
                return (
                  <div
                    key={b.name}
                    title={b.name}
                    className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${b.color}20` }}
                  >
                    <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: b.color }} />
                  </div>
                );
              })}
              {user.badge_count > 3 && (
                <span className="text-xs text-gray-400 ml-1">+{user.badge_count - 3}</span>
              )}
              {user.badge_count === 0 && <span className="text-xs text-gray-500">No badges</span>}
            </div>
          </div>
        </div>
      ))}
      {xpBoard.length === 0 && <div className="text-center py-12 text-gray-400">No XP data yet</div>}
    </div>
  );
}

function Stat({ label, value, valueClass = "text-white text-sm" }) {
  return (
    <div className="text-center sm:text-left">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={valueClass}>{value}</div>
    </div>
  );
}
